<script setup lang="ts">
import { Link } from '@inertiajs/vue3';
import { ZapIcon } from 'lucide-vue-next';
import Button from '@/components/ui/button/Button.vue';
import type { MainBannerSlide } from '@/types/main-banner';

interface Props {
  slide: MainBannerSlide;
  isMainH1?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  isMainH1: false,
});

const formatPrice = (price: number) => {
  return new Intl.NumberFormat('ru-RU').format(price) + ' ₽';
};
</script>

<template>
  <div
    v-if="slide.image_only"
    class="relative h-screen w-full overflow-hidden lg:aspect-video lg:h-[50vh] lg:min-h-122"
  >
    <picture class="absolute inset-0 h-screen w-screen lg:h-full">
      <source v-if="slide.image_mobile" :srcset="slide.image_mobile" media="(max-width: 767px)" />
      <source v-if="slide.image_tablet" :srcset="slide.image_tablet" media="(max-width: 1023px)" />
      <img
        :src="slide.image"
        :alt="slide.name ?? `Главный банер - ${slide.id}`"
        fetchpriority="high"
        class="h-screen w-screen object-cover object-center lg:h-full"
      />
    </picture>

    <Link
      v-if="slide.product"
      prefetch
      :href="slide.product.link"
      class="group absolute right-4 bottom-4 left-4 rounded-xl bg-white/95 p-4 shadow-lg backdrop-blur-sm hover:bg-primary lg:right-6 lg:bottom-6 lg:left-6"
    >
      <div class="flex items-center justify-between gap-3">
        <div class="flex items-center gap-3">
          <div class="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-green-100">
            <ZapIcon class="h-5 w-5 text-green-600" />
          </div>
          <div>
            <p
              v-show="slide.product.category"
              class="text-xs font-semibold text-muted-foreground uppercase group-hover:text-secondary"
            >
              {{ slide.product.category }}
            </p>
            <p class="line-clamp-2 font-semibold text-foreground">
              {{ slide.product.name }}
            </p>
          </div>
        </div>
        <div class="text-right whitespace-nowrap">
          <p class="text-xl font-bold text-green-600 group-hover:text-white">
            {{ formatPrice(slide.product.price) }}
          </p>
          <p class="text-xs text-muted-foreground group-hover:text-secondary">
            {{ slide.product.in_stock ? 'В наличии' : 'Под заказ' }}
          </p>
        </div>
      </div>
    </Link>
  </div>

  <div v-else class="grid h-full grid-cols-1 items-center gap-8 max-sm:h-full lg:grid-cols-7 lg:gap-12">
    <div class="order-2 mx-4 px-4 max-sm:h-full lg:order-1 lg:col-span-4 lg:mx-auto lg:px-8 lg:pt-8 xl:px-12">
      <span
        v-if="slide.badge"
        class="mb-4 inline-block rounded-full bg-green-100 px-3 py-1 text-sm font-medium text-green-800"
      >
        {{ slide.badge }}
      </span>

      <component
        v-if="slide.name"
        :is="isMainH1 ? 'h1' : 'h2'"
        class="mb-6 text-4xl leading-tight font-bold text-foreground lg:text-5xl xl:text-6xl"
      >
        <template v-for="(part, index) in slide.name.split('\\n')" :key="index">
          <span :class="{ 'text-green-600': index === 1 && slide.name!.includes(' ') }">
            {{ part }}
          </span>
          <br v-if="index < slide.name.split('\\n').length - 1" />
        </template>
      </component>

      <p v-if="slide.description" class="mb-8 max-w-lg text-lg text-muted-foreground">
        {{ slide.description }}
      </p>

      <div class="flex-1"></div>

      <div v-if="slide.buttons?.length" class="mb-8 flex flex-wrap gap-4">
        <Button
          v-for="(button, index) in slide.buttons"
          :key="index"
          as-child
          :variant="button.variant || 'default'"
          size="lg"
          :class="{
            'border-2': button.variant === 'outline',
          }"
        >
          <Link :href="button.url" target="_blank">
            {{ button.text }}
          </Link>
        </Button>
      </div>
    </div>

    <div class="relative order-1 h-full lg:order-2 lg:col-span-3 lg:h-[50vh]">
      <div class="relative h-full overflow-hidden lg:rounded-l-2xl">
        <img :src="slide.image" :alt="slide.name ?? ''" fetchpriority="high" class="h-full w-full object-cover" />
      </div>

      <Link
        v-if="slide.product"
        prefetch
        :href="slide.product.link"
        class="group absolute right-4 bottom-4 left-4 rounded-xl bg-white/95 p-4 shadow-lg backdrop-blur-sm hover:bg-primary lg:right-6 lg:bottom-6 lg:left-6"
      >
        <div class="flex items-center justify-between gap-3">
          <div class="flex items-center gap-3">
            <div class="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-green-100">
              <ZapIcon class="h-5 w-5 text-green-600" />
            </div>
            <div>
              <p
                v-show="slide.product.category"
                class="text-xs font-semibold text-muted-foreground uppercase group-hover:text-secondary"
              >
                {{ slide.product.category }}
              </p>
              <p class="line-clamp-2 font-semibold text-foreground">
                {{ slide.product.name }}
              </p>
            </div>
          </div>
          <div class="text-right whitespace-nowrap">
            <p class="text-xl font-bold text-green-600 group-hover:text-white">
              {{ formatPrice(slide.product.price) }}
            </p>
            <p class="text-xs text-muted-foreground group-hover:text-secondary">
              {{ slide.product.in_stock ? 'В наличии' : 'Под заказ' }}
            </p>
          </div>
        </div>
      </Link>
    </div>
  </div>
</template>
